import { isDensityPreference, type DensityPreference } from "./types";

/**
 * Per-device preference, kept in localStorage like the theme so it applies
 * before login and needs no backend. The inline anti-flash script in
 * index.html reads the same key; keep them in sync.
 */
export const DENSITY_STORAGE_KEY = "caseblink.density";

/** Set once the one-time "text is larger now" prompt has been shown. */
export const DENSITY_NOTICE_STORAGE_KEY = "caseblink.density.noticeSeen";

export const DEFAULT_DENSITY_PREFERENCE: DensityPreference = "comfortable";

const storage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    // Access can throw under strict privacy settings.
    return null;
  }
};

/** The stored choice, or `null` when the user has never picked one. */
export function readStoredDensity(): DensityPreference | null {
  try {
    const raw = storage()?.getItem(DENSITY_STORAGE_KEY);
    return isDensityPreference(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function readDensityPreference(): DensityPreference {
  return readStoredDensity() ?? DEFAULT_DENSITY_PREFERENCE;
}

export function writeDensityPreference(preference: DensityPreference): void {
  try {
    storage()?.setItem(DENSITY_STORAGE_KEY, preference);
  } catch {
    // Quota / privacy mode: the in-memory preference still applies for the session.
  }
}

export function hasSeenDensityNotice(): boolean {
  try {
    return storage()?.getItem(DENSITY_NOTICE_STORAGE_KEY) === "1";
  } catch {
    return true;
  }
}

export function markDensityNoticeSeen(): void {
  try {
    storage()?.setItem(DENSITY_NOTICE_STORAGE_KEY, "1");
  } catch {
    // Not persisted: the prompt may show once more next session.
  }
}
